// plotLattice.ts - the plot mesh's vertex arithmetic, thread-agnostic.
//
// Lifted out of dtmPlot.ts so the tile worker can call it without pulling
// three.js, the rover and the rock field into its bundle. dtmPlot.ts's
// `hexLattice` wraps what comes back in a BufferGeometry with no copy, and the
// worker hands the same two buffers over as transferables, so a vertex cannot
// depend on which thread produced it.

const SQRT3 = Math.sqrt(3);

/** One parent hex of a cluster, in axial coordinates. */
export interface LatticePlace {
  readonly q: number;
  readonly r: number;
}

/** What both threads answer a lattice request with. */
export interface LatticeBuffers {
  readonly position: Float32Array<ArrayBuffer>;
  readonly index: Uint32Array<ArrayBuffer>;
}

/**
 * Triangular lattice filling a flat-top hexagon of radius R, in metres.
 *
 * Axial coords (q, r) with |q|, |r|, |q+r| <= N give exactly the hexagon:
 * 3N^2+3N+1 vertices, boundary dead on the six edges. Positions land in XZ with
 * across-flats on X (the narrow axis of a HiRISE swath) and across-corners on Z.
 *
 * Dedup by INTEGER axial coordinate, through a flat table. This used to key a
 * Set and a Map on `${q},${r}` and then `split(',')` the string back into
 * numbers twice per cell - 75 ms for one plot at 192 rings and 413 ms for a
 * 36-plot region, spent entirely on string churn.
 *
 * A parent hex one step east sits at (R*sqrt3, 0); one step "south-east" at
 * (R*sqrt3/2, 1.5R). Expressed in the VERTEX lattice those are the integer
 * axial offsets (-N, 2N) and (N, N). Because they are integers, adjoining plots
 * share their boundary vertices exactly - one dedup pass and the cluster is a
 * single continuous surface with no seam to hide and no cracks to chase.
 */
export function buildLatticeBuffers(
  R: number,
  N: number,
  places: readonly LatticePlace[],
  origin: { readonly x: number; readonly z: number },
): LatticeBuffers {
  const a = R / N;
  let qmin = Infinity;
  let qmax = -Infinity;
  let rmin = Infinity;
  let rmax = -Infinity;
  for (const p of places) {
    const qoff = N * (p.r - p.q);
    const roff = N * (2 * p.q + p.r);
    if (qoff - N < qmin) qmin = qoff - N;
    if (qoff + N > qmax) qmax = qoff + N;
    if (roff - N < rmin) rmin = roff - N;
    if (roff + N > rmax) rmax = roff + N;
  }
  const rs = rmax - rmin + 1;
  const slot = new Int32Array((qmax - qmin + 1) * rs).fill(-1);

  let n = 0;
  for (const p of places) {
    const qoff = N * (p.r - p.q);
    const roff = N * (2 * p.q + p.r);
    for (let r = -N; r <= N; r++) {
      for (let q = -N; q <= N; q++) {
        if (Math.abs(q + r) > N) continue;
        const k = (q + qoff - qmin) * rs + (r + roff - rmin);
        if (slot[k] !== -1) continue;   // shared with a neighbouring plot
        slot[k] = n++;
      }
    }
  }

  // Positions are written BY slot index rather than pushed, so the walk order
  // is free and the array is sized exactly once.
  const position = new Float32Array(n * 3);
  const at = (gq: number, gr: number): number => {
    if (gq < qmin || gq > qmax || gr < rmin || gr > rmax) return -1;
    return slot[(gq - qmin) * rs + (gr - rmin)]!;
  };
  let tris = 0;
  for (let k = 0; k < slot.length; k++) {
    const v0 = slot[k]!;
    if (v0 === -1) continue;
    const gq = qmin + Math.floor(k / rs);
    const gr = rmin + (k % rs);
    position[v0 * 3] = origin.x + gr * a * (SQRT3 / 2);
    position[v0 * 3 + 2] = origin.z + (gq + gr / 2) * a;
    const vR = at(gq + 1, gr);
    const vD = at(gq, gr + 1);
    const vDL = at(gq - 1, gr + 1);
    if (vR !== -1 && vD !== -1) tris++;
    if (vD !== -1 && vDL !== -1) tris++;
  }

  const index = new Uint32Array(tris * 3);
  let w = 0;
  for (let k = 0; k < slot.length; k++) {
    const v0 = slot[k]!;
    if (v0 === -1) continue;
    const gq = qmin + Math.floor(k / rs);
    const gr = rmin + (k % rs);
    const vR = at(gq + 1, gr);
    const vD = at(gq, gr + 1);
    const vDL = at(gq - 1, gr + 1);
    // Wind counter-clockwise seen from +Y. The other order gives (0,-ka,0) -
    // every triangle facing the ground, back-face culled, and a plot you can
    // see straight through into the inside of its own walls.
    if (vR !== -1 && vD !== -1) {
      index[w++] = v0; index[w++] = vR; index[w++] = vD;
    }
    if (vD !== -1 && vDL !== -1) {
      index[w++] = v0; index[w++] = vD; index[w++] = vDL;
    }
  }
  return { position, index };
}
